import { createElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { MarkerDefinitions, SvgTextureDefsDefinitions } from '@ardoq/icons';
import {
  getAllFontFaceRules,
  inlineFontFaceRules,
} from './fontFaceToDataUrlUtil';
import { getCssRulesFromStylesheet } from './getCssRulesFromStylesheet';
import { cloneImageColorFilters, elementToSvgString } from './exportUtil';
import { logWarn } from '@ardoq/logging';
import { isSafari } from '@ardoq/common-helpers';
import type { GetStyleSheetsFn, PartialCSSStyleSheet, Point } from './types';
import * as profiling from '@ardoq/profiling';

export const serializeAndWrapInSVG = (
  profilingTransaction: profiling.Transaction,
  clonedDOM: HTMLElement | SVGElement,
  width: number,
  height: number,
  defs: string,
  position?: Point
) => {
  const serializeAndWrapSpan = profiling.startSpan(
    profilingTransaction,
    'serializeAndWrapInSVG'
  );

  const clonedImageColorFilters = cloneImageColorFilters();

  const svgString = elementToSvgString(
    clonedDOM,
    width,
    height,
    `${defs}${renderToStaticMarkup(
      createElement(MarkerDefinitions)
    )}${renderToStaticMarkup(createElement(SvgTextureDefsDefinitions))}${
      clonedImageColorFilters ? clonedImageColorFilters : ''
    }`,
    position
  );
  profiling.endSpan(serializeAndWrapSpan);

  return svgString;
};

const matchRule = (el: Element, cssRule: CSSStyleRule) => {
  const matchFn = (el.matches || el.webkitMatchesSelector).bind(el);
  try {
    return matchFn(cssRule.selectorText);
  } catch (error) {
    if (isSafari() && cssRule.selectorText.includes('::-webkit')) {
      // expected - this is a bug in Safari. Element.matches throws a SyntaxError any time rule is checking for a webkit extension pseudo-element.
    } else {
      logWarn(
        error instanceof Error
          ? error
          : new Error('Error matching css rule during export.'),
        'Error matching css rule during export.',
        { selectorText: cssRule.selectorText }
      );
    }
    return false;
  }
};

export const getFontFaceRules = async (sheets: PartialCSSStyleSheet[]) => {
  const [fontFaceRulesDataURL, fontFaceRulesResource] =
    getAllFontFaceRules(sheets);
  const inlinedFontFaceRules = await inlineFontFaceRules(fontFaceRulesResource);
  return [...inlinedFontFaceRules, ...fontFaceRulesDataURL];
};

const getFontStyles = async (
  element: Element,
  sheets: PartialCSSStyleSheet[]
) => {
  const fontStyleRules = sheets.flatMap(sheet => {
    return getCssRulesFromStylesheet(sheet).filter(
      rule =>
        !(rule instanceof CSSFontFaceRule) &&
        (rule.cssText.startsWith('.fa-') ||
          rule.cssText.includes('svg') ||
          rule.cssText.includes('.visual-diff-') ||
          matchRule(element, rule as CSSStyleRule))
    );
  });
  const fontFaceRules = await getFontFaceRules(sheets);
  return [...fontFaceRules, ...fontStyleRules]
    .map(rule => rule.cssText)
    .join('\n');
};
const escapeXmlContent = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

export const getStyleElementHtml = (styles: string): string =>
  `<style type="text/css">${escapeXmlContent(styles)}</style>`;
export const getStyles = async (
  element: Element,
  getStyleSheets: GetStyleSheetsFn,
  additionalStyles: string,
  profilingTransaction: profiling.Transaction
) => {
  const getStylesSpan = profiling.startSpan(
    profilingTransaction,
    'getStyleSheets'
  );

  // if a style sheet is from the same domain, you will be able to access cssRules via JS,
  // otherwise Chrome will throw a CORS-related error (commented on March 16th, 2022)
  const sheets = await getStyleSheets();
  const fontStyles = await getFontStyles(element, sheets);

  profiling.endSpan(getStylesSpan);
  return getStyleElementHtml(`${fontStyles}${additionalStyles}`);
};
